/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { SvgEvvLogoLight } from "../../assets/svg";
import { useAppSelector, useAppDispatch } from "../../core/redux/ReduxStore";
import { NavConfig } from "../../config/navConfig";
import { useState, useEffect } from "react";
import { ActionUI } from "../../core/redux";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { Button, Drawer } from "rsuite";
import { StorageAuth } from "../../core/localStorage";
import { useAuthFunc } from "../../libs/hooks";
import { AxiosAuth } from "../../core/axios";
import { ApiResponseContainer } from "../../models/api";
import { Utility } from "../../config";
import { Stripe } from "@stripe/stripe-js";

type NavButtonProps = {
  label: string;
  path: string;
  svgIcon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  activeOnPaths?: string[];
};

const NavButton = (props: NavButtonProps): JSX.Element => {
  const routeLocation = useLocation();
  const isActive = routeLocation.pathname.startsWith(props.path);
  const dispatch = useAppDispatch();

  return (
    <li className="nav-item">
      <Link
        to={props.path}
        className={`nav-link${isActive ? " active" : ""} text-decoration-none`}
        aria-current="page"
        onClick={() => dispatch(ActionUI.closeSideNav())}
      >
        <div className="d-flex align-item-center ">
          <div className="svg-container">
            {props.svgIcon && <props.svgIcon />}
          </div>
          <p>{props.label}</p>
        </div>
      </Link>
    </li>
  );
};

const DropdownButton = (props: Partial<NavButtonProps>): JSX.Element => {
  const routeLocation = useLocation();
  const isActive = props.path && routeLocation.pathname.startsWith(props.path);
  const dispatch = useAppDispatch();

  return (
    <li className="dropdown-item p-0">
      <Link
        to={props.path || "#"}
        className={`p-3 m-0 d-block text-decoration-none text-green-200 dropdown-link${
          isActive ? " active" : ""
        }`}
        aria-current="page"
        onClick={() => dispatch(ActionUI.closeSideNav())}
      >
        {props.label}
      </Link>
    </li>
  );
};

const NavButtonDropdown = (
  props: React.PropsWithChildren<Partial<NavButtonProps>>
): JSX.Element => {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(
    !props.activeOnPaths?.some((item) => pathname.startsWith(item))
  );

  useEffect(
    () =>
      setCollapsed(
        !props.activeOnPaths?.some((item) => pathname.startsWith(item))
      ),
    [pathname, props.activeOnPaths]
  );

  return (
    <li className="nav-item mb-3">
      <div
        className={`nav-link p-0 mb-0 click text-decoration-none${
          collapsed ? "" : " active"
        }`}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="d-flex side-nav-dropdown-header align-item-center">
          <div className="svg-container">
            {props.svgIcon && <props.svgIcon />}
          </div>

          <p className="d-flex justify-content-between w-100 align-items-center">
            {props.label}
            <i className={`fas fa-chevron-${collapsed ? "down" : "up"}`} />
          </p>
        </div>
        <div className={`dropdown dropdown-${collapsed ? "hidden" : "show"}`}>
          {props.children}
        </div>
      </div>
    </li>
  );
};

const NavItems = () => {
  const { menuItems } = useAppSelector((state) => state.user);

  return (
    <ul className="nav flex-column">
      {menuItems.map((item) =>
        item.children ? (
          <NavButtonDropdown
            key={item.id}
            label={item.displayName}
            svgIcon={NavConfig[item.id].icon}
            activeOnPaths={NavConfig[item.id].activeOnPaths}
          >
            <ul className="dropdown-nav-items p-0">
              {item.children.map((dropdownItem) => (
                <DropdownButton
                  key={dropdownItem.id}
                  label={dropdownItem.displayName}
                  path={NavConfig[dropdownItem.id].path}
                />
              ))}
            </ul>
          </NavButtonDropdown>
        ) : (
          <NavButton
            key={item.id}
            label={item.displayName}
            path={NavConfig[item.id].path}
            svgIcon={NavConfig[item.id].icon}
          />
        )
      )}
    </ul>
  );
};

export const LeftNav = (): JSX.Element => {
  const { isSiteNavOpen } = useAppSelector((state) => state.ui);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const dispatch = useAppDispatch();
  const { ProfilePic } = StorageAuth;
  const stripe = useRef<Stripe>();
  const { firstName, lastName, userRole } = useAppSelector(
    (state) => state.auth
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { company } = useAppSelector((state) => state.company);
  const { menuItems } = useAppSelector((state) => state.user);

  const { isBusy, logOut } = useAuthFunc();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      dispatch(ActionUI.closeSideNav());
    };

    window.addEventListener("resize", handleResize);
    stripe.current = Utility.getStripe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const checkout = () => {
    AxiosAuth.post<ApiResponseContainer<string>>(
      `${AppRouteApi.SuperUserRoot}/Payment/OneTimeCheckoutSessionToPurchasePremiumUser`
    ).then((res) => {
      if (res.data?.data && !!stripe)
        stripe?.current?.redirectToCheckout({
          sessionId: res.data?.data,
        });
    });
  };

  return !isMobile ? (
    <div className={`site-nav`}>
      <div className="logo-container">
        <SvgEvvLogoLight className="img-fluid logo-container-img" />
      </div>
      <ul className="nav flex-column">
        {menuItems.map((item) =>
          item.children ? (
            <NavButtonDropdown
              key={item.id}
              label={item.displayName}
              svgIcon={NavConfig[item.id].icon}
              activeOnPaths={NavConfig[item.id].activeOnPaths}
            >
              <ul className="dropdown-nav-items p-0">
                {item.children.map((dropdownItem) => (
                  <DropdownButton
                    key={dropdownItem.id}
                    label={dropdownItem.displayName}
                    path={NavConfig[dropdownItem.id].path}
                  />
                ))}
              </ul>
            </NavButtonDropdown>
          ) : (
            <NavButton
              key={item.id}
              label={item.displayName}
              path={NavConfig[item.id].path}
              svgIcon={NavConfig[item.id].icon}
            />
          )
        )}
      </ul>
      {/* {!!company?.subscription &&
        company.subscription.subscriptionType === "Trial" && (
          <div className="d-flex justify-content-center px-5 mt-3">
            <Button
              className="btn btn-green text-green-200 bg-white w-100"
              onClick={checkout}
            >
              Subscribe
            </Button>
          </div>
        )} */}
    </div>
  ) : (
    <Drawer
      open={isSiteNavOpen}
      onClose={() => dispatch(ActionUI.closeSideNav())}
      placement="left"
      full
    >
      <Drawer.Body className="mobile-site-nav d-flex flex-column">
        <p
          className="side-nav-close-button fs-1 px-3"
          onClick={() => dispatch(ActionUI.closeSideNav())}
        >
          X
        </p>
        <div
          className={
            userRole?.match("SuperAdmin")
              ? "user-info-sa d-flex align-items-center"
              : "user-info d-flex align-items-center"
          }
        >
          <img
            src={
              ProfilePic
                ? AppRouteApi.Media.ByPath(ProfilePic)
                : "/img/admin-profile.svg"
            }
            alt=""
            width={50}
            height={50}
            className="rounded-circle object-fit-cover my-2 click"
          />
          <div className="ms-3">
            <h5>
              {firstName} {lastName}
            </h5>
            <p className="">{userRole}</p>
          </div>
        </div>
        <NavItems />
        <Button
          color="red"
          appearance="ghost"
          className="btn-block mx-3 my-3 logout-button"
          onClick={() => {
            !isBusy && logOut();
          }}
        >
          Logout
        </Button>
        <div className="footer mt-auto fs">
          <div className="d-flex justify-content-center">
            <a
              href={AppRouteUi.PrivacyPolicy.Root()}
              className="btn btn-link text-capitalize footer-btn-link"
            >
              privacy policy
            </a>
          </div>
          <p className="text-center">webEVV &copy;2021. All Rights Reserved</p>
        </div>
      </Drawer.Body>
    </Drawer>
  );
};
